import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as LZString from 'lz-string';
import { filter } from 'rxjs';
import { SiaSessionApi } from '../apis/sia-session.api';
import { SiaSessionParam } from '../interfaces/sia-session-param';
import { jsonDeepClone } from './jsonDeepClone';
import { SiaUsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class SiaSessionService {
  private route = inject(ActivatedRoute);
  private userService = inject(SiaUsersService);
  private siaSessionApi = inject(SiaSessionApi);
  private router = inject(Router);

  private _weakMemory: any = {};
  private _sessionMemory: any = {};

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    const router = this.router;

    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      let guid = this.getDotConcatBackGuid()?.split('.') ?? [];
      if (!guid) return;
      Object.keys(sessionStorage).map((c) => {
        const guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
        const match = c.match(guidRegex);
        const containsGuid = match ? match.some((m) => guid.includes(m)) : false;
        if (
          c &&
          !containsGuid &&
          'routehistory|APIURLS|token|latestpagedata|instance-identifier|device-type|latest-guid|user-language'.indexOf(
            c,
          ) == -1
        ) {
          this._sessionMemory[c] = sessionStorage.getItem(c);

          sessionStorage.removeItem(c);
        }
      });
    });
  }

  private getDotConcatBackGuid(): string | undefined {
    let guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
    let guid = ((this.route as any)._routerState.snapshot.url as string).match(guidRegex);
    return guid?.join('.');
  }

  private getLastModifiedDateKey() {
    return 'last-modified-date';
  }

  private getAdapterSessionKey(adapter: string, guid: string) {
    return 'adapter-' + guid + '-' + adapter.toLowerCase();
  }
  private getTranslationsIdSessionKey() {
    return 'translations';
  }

  private getImpersonateLogoutBackGuidKey() {
    return 'impersonate-logout-back';
  }

  private getRouterSessionKey(p: string) {
    return 'route-' + p.toLowerCase();
  }

  private getRouterParamsSessionKey(p?: string | null, aiParams: boolean = false) {
    let guid: string | undefined = '';
    if (!p) {
      guid = this.getDotConcatBackGuid();
    } else {
      guid = p;
    }
    return (aiParams ? 'ai-' : '') + 'params-' + guid?.toLowerCase();
  }

  private getPermissionSessionKey(id: number, key: string) {
    return 'permission-' + id.toString() + '-' + key.toLowerCase();
  }

  get(k: string | null): any {
    if (!k) return null;
    let i = this._sessionMemory[k];
    if (!i) {
      i = sessionStorage.getItem(k ?? '');
      if (i) {
        this._sessionMemory[k] = i; // sincronizza la memoria
      }
    }
    if (!i) {
      return null;
    }
    let guid = this.getDotConcatBackGuid()?.split('.') ?? [];
    const guidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
    const match = k.match(guidRegex);
    const containsGuid = match ? match.some((m) => guid.includes(m)) : false;
    if (containsGuid) {
      sessionStorage.setItem(k, i);
    }
    return JSON.parse(i);
  }

  set(k: string | null, data: any) {
    if (k) {
      let p = JSON.stringify(data);
      try {
        this._sessionMemory[k] = p;
        sessionStorage.setItem(k, p);
      } catch (e) {
        console.error(e);
      }
    }
  }

  clear(keyType: string | undefined | null) {
    if (!keyType) return;
    sessionStorage.removeItem(keyType);
    Object.keys(this._sessionMemory).map((c) => {
      if (c && c.indexOf(keyType) >= 0) {
        this._sessionMemory[c] = null;
      }
    });
  }

  clearLastDateModified() {
    this.clear('last-modified-date');
  }

  getLastModifiedDate() {
    let k = this.getLastModifiedDateKey();
    return this.get(k);
  }

  setLastModifiedDate(date: string) {
    let k = this.getLastModifiedDateKey();
    this.set(k, date);
  }
  setTranslationsDictionary(translations: any) {
    let k = this.getTranslationsIdSessionKey();
    this.set(k, translations);
  }

  getTranslationsDictionary() {
    let k = this.getTranslationsIdSessionKey();
    return this.get(k);
  }

  addTranslationToDictionary(toTranslate: string, translation: string) {
    let k = this.getTranslationsIdSessionKey();
    const translations: Record<string, string> = this.get(k) || {};

    translations[toTranslate] = translation;

    this.clearTranslations();
    this.set(k, translations);
  }
  clearTranslations() {
    this.clear('translations');
  }
  clearPermissions() {
    this.clear('permission');
  }

  getAdapterBackGuid(adapterId: string, guid: string) {
    let k = this.getAdapterSessionKey(adapterId, guid);
    return this.get(k);
  }

  setAdapterBackGuid(adapterId: string, guid: string) {
    let k = this.getAdapterSessionKey(adapterId, guid);
    return this.set(k, guid);
  }

  clearAdapterBackGuid(adapterId: string, guid: string) {
    let k = this.getAdapterSessionKey(adapterId, guid);
    this.clear(k);
  }

  setBackUrl(
    guid: string,
    data: {
      url: string;
      componentId: string | null;
      guid: string;
      params: SiaSessionParam[];
    },
  ) {
    let k = this.getRouterSessionKey(guid);
    this.set(k, JSON.stringify(data));
  }

  getBackUrl(): {
    url: string;
    componentId: string | null;
    params: SiaSessionParam[];
    guid: string;
    mode: any;
  } | null {
    var guid = this.getDotConcatBackGuid();
    if (!guid) return null;
    let k = this.getRouterSessionKey(guid);
    let res = this.get(k);
    return JSON.parse(res);
  }
  getBackUrlGuid(guid: string) {
    let k = this.getRouterSessionKey(guid);
    let res = this.get(k);
    return JSON.parse(res);
  }
  setSessionParams(guid: string | undefined | null, params: SiaSessionParam[], aiParams: boolean = false) {
    params.map((x) => (x.id = x.id?.toLocaleLowerCase()));
    let k = this.getRouterParamsSessionKey(guid, aiParams);
    this.set(k, params);
  }

  removeSessionParam(key: string, guid: string | null = null) {
    var params = this.getSessionParams(guid);
    if (!params) return;
    var filtered = params.filter((x) => x.id != key);
    this.setSessionParams(guid, filtered);
  }

  updateSessionParams(guid: string | undefined | null, params: SiaSessionParam[], aiParams: boolean = false) {
    params = jsonDeepClone(params);
    params.map((x) => (x.id = x.id?.toLowerCase()));
    if (!params) return;
    var oldParams = this.getSessionParams(guid, aiParams);
    if (!oldParams || oldParams.length == 0) {
      this.setSessionParams(guid, params, aiParams);
      return;
    }
    oldParams.forEach((element, index) => {
      var newIndex = params?.findIndex((x) => x.id?.toLowerCase() == element.id?.toLowerCase()) ?? -1;
      if (newIndex != -1) {
        Object.assign(element, params[newIndex]);
        params.splice(newIndex, 1);
      }
    });
    params = [...oldParams, ...params];
    this.setSessionParams(guid, params, aiParams);
  }
  getSessionParams(
    reqGuid: string | undefined | null = undefined,
    aiParams: boolean = false,
  ): SiaSessionParam[] | null {
    var guid = reqGuid;
    if (!guid) {
      guid = this.getDotConcatBackGuid();
    }
    if (!guid) return null;
    let k = this.getRouterParamsSessionKey(guid, aiParams);
    var params = this.get(k);
    if (!params) return [];
    return params;
  }
  // TODO Rendere obbligatorio il GUID`
  getSessionParamValue(paramName: string, guid?: string | null): any {
    if (!guid) {
      guid = sessionStorage.getItem('latest-guid');
    }
    if (!paramName) return undefined;
    paramName = paramName.split('$').join(''); //pulisco se c'è il $ usato per convenzione per indicare il nome di un parametro in sessione
    var params = this.getSessionParams(guid);
    if (!params) return undefined;
    var filtered = params.filter((p) => p.id?.toLowerCase() == paramName.toLowerCase());
    if (filtered && filtered.length > 0) {
      return filtered[0].value;
    }
    return undefined;
  }
  clearAlSiaSessionParams() {
    this.clear('params');
  }

  clearSessionParams() {
    var guid = this.getDotConcatBackGuid();
    if (!guid) return;
    let k = this.getRouterParamsSessionKey(guid);
    this.clear(k);
  }

  getPermission(id: number, componentId: string) {
    let k = this.getPermissionSessionKey(id, componentId);
    return this.get(k);
  }
  setPermission(id: number, componentId: string, data: any) {
    let k = this.getPermissionSessionKey(id, componentId);
    this.set(k, data);
  }

  clearPermission(id: number, componentId: string) {
    let k = this.getPermissionSessionKey(id, componentId);
    this.clear(k);
  }

  clearRoutes() {
    this.clear('route');
  }

  clearActionMode() {
    this.clear('actionMode');
  }

  setImpersonateLogoutBackGuid(backImpersonate: { guid: string; componentId: string }) {
    let k = this.getImpersonateLogoutBackGuidKey();
    this.set(k, backImpersonate);
  }

  getImpersonateLogoutBackGuid(): { guid: string; componentId: string } {
    let k = this.getImpersonateLogoutBackGuidKey();
    return this.get(k);
  }

  clearImpersonateLogoutBackGuid() {
    let k = this.getImpersonateLogoutBackGuidKey();
    this.clear(k);
  }

  getFilterMessage(guid: string) {
    return this.get(`filter-message-${guid}`);
  }

  setFilterMessage(guid: string, message: string) {
    this.set(`filter-message-${guid}`, message);
  }

  getTitle(guid: string) {
    return this.get(`title-${guid}`);
  }

  setTitle(guid: string, title: string) {
    this.set(`title-${guid}`, title);
  }

  getSubtitle(guid: string) {
    return this.get(`subtitle-${guid}`);
  }

  setSubtitle(guid: string, subtitle: string) {
    this.set(`subtitle-${guid}`, subtitle);
  }

  getTitleAdditionalInformation(guid: string) {
    return this.get(`title-additional-information-${guid}`);
  }

  setTitleAdditionalInformation(guid: string, subtitle: string) {
    this.set(`title-additional-information-${guid}`, subtitle);
  }

  getFilterSelectedChip(guid: string) {
    return this.get(`filter-selected-chip-${guid}`);
  }

  setFilterSelectedChip(guid: string, chips: string | undefined) {
    this.set(`filter-selected-chip-${guid}`, chips);
  }

  setFilterChips(guid: string, chips: any[]) {
    this.set(`filter-chips-${guid}`, chips);
  }

  getFilterChips(guid: string) {
    return this.get(`filter-chips-${guid}`);
  }

  getPageAndUserDefaultSessionParams() {
    let params: any[] = [];
    let pageParams = this.getSessionParams();
    if (pageParams && pageParams.length > 0) {
      params = [...params, ...pageParams];
    }
    //BACC Perchè carico usersDefaults che sono già in un altro parametro in sessione???
    let userParams = this.userService.getUserDefaultsData();
    if (userParams && userParams.length > 0) {
      params = [...params, ...userParams];
    }
    return params;
  }

  weakMemorySet(key: string, data: any) {
    this._weakMemory[key] = jsonDeepClone(data);
  }

  weakMemoryGet(key: string) {
    return jsonDeepClone(this._weakMemory[key]);
  }
  weakMemoryClearByContains(keyPart: string) {
    for (let key in this._weakMemory) {
      if (key.toLowerCase().includes(keyPart.toLowerCase())) {
        this.weakMemoryClear(key);
      }
    }
  }

  weakMemoryClear(key: string) {
    this._weakMemory[key] = null;
  }

  async saveStorage() {
    let instanceId = sessionStorage.getItem('instance-identifier');
    if (!instanceId) {
      return;
    }
    let json = JSON.stringify(this._sessionMemory);
    var compress = LZString.compressToBase64(json);
    await this.siaSessionApi.saveStorage(instanceId, compress);
  }

  async loadStorage() {
    let instanceId = sessionStorage.getItem('instance-identifier');
    if (!instanceId) {
      return;
    }
    let storage = await this.siaSessionApi.getStorage(instanceId);
    storage = LZString.decompressFromBase64(storage);
    let memory = JSON.parse(storage || '{}');
    // update memory by keys
    Object.keys(memory).forEach((key) => {
      this._sessionMemory[key] = memory[key];
    });
  }

  clearStorage() {
    this._weakMemory = {};
    this._sessionMemory = {};
  }
}
